import { useRef, useState } from 'react'
import { Upload, X, FileText, ImageIcon, Loader2 } from 'lucide-react'
import { cn } from '@/lib/cn'

interface Props {
  /** Tipo aceito: imagem (jpg/png/webp) ou PDF. */
  accept: 'image' | 'pdf'
  /** URL atual do arquivo (preview). Quando preenchido, mostra a imagem ou link do PDF. */
  currentUrl: string | null
  /** Nome de exibição quando há arquivo (ex.: "Contrato.pdf"). Opcional. */
  currentName?: string | null
  /** Disparado ao selecionar um novo arquivo. */
  onSelect: (file: File) => Promise<void> | void
  /** Disparado ao clicar no X para remover o arquivo atual. */
  onRemove?: () => Promise<void> | void
  /** Tamanho máximo em MB. Default: 5 para imagem, 10 para pdf. */
  maxMB?: number
  label?: string
  required?: boolean
  disabled?: boolean
  /** Texto auxiliar abaixo do drop. */
  hint?: string
  /** Mensagem de erro exibida em vermelho. */
  error?: string
}

/**
 * Componente de upload de arquivo com drop area e preview.
 *
 * - `accept='image'` → aceita jpg/png/webp e mostra preview da imagem
 * - `accept='pdf'`   → aceita PDF e mostra ícone + nome
 *
 * O componente NÃO faz a request — quem usa entrega `onSelect(file)` e cuida
 * da chamada ao backend (com loading externo). Mostra spinner se a promise
 * retornada por `onSelect` ainda estiver pendente.
 */
export function FileUpload({
  accept,
  currentUrl,
  currentName,
  onSelect,
  onRemove,
  maxMB,
  label,
  required,
  disabled,
  hint,
  error,
}: Props) {
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [dragging, setDragging] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [removing, setRemoving] = useState(false)
  const [localError, setLocalError] = useState<string | null>(null)

  const limit = maxMB ?? (accept === 'image' ? 5 : 10)
  const acceptAttr = accept === 'image' ? 'image/jpeg,image/png,image/webp' : 'application/pdf'
  const acceptLabel = accept === 'image' ? 'JPG, PNG ou WEBP até ' + limit + ' MB' : 'PDF até ' + limit + ' MB'

  async function handleFile(file: File | undefined | null) {
    setLocalError(null)
    if (!file) return

    // Valida tipo
    const mime = file.type
    const okType = accept === 'image'
      ? ['image/jpeg', 'image/png', 'image/webp'].includes(mime)
      : mime === 'application/pdf'
    if (!okType) {
      setLocalError(`Tipo de arquivo inválido. Aceito: ${acceptLabel}.`)
      return
    }

    // Valida tamanho
    if (file.size > limit * 1024 * 1024) {
      setLocalError(`Arquivo muito grande. Máximo ${limit} MB.`)
      return
    }

    setUploading(true)
    try {
      await onSelect(file)
    } catch (e: unknown) {
      setLocalError(e instanceof Error ? e.message : 'Falha ao enviar o arquivo.')
    } finally {
      setUploading(false)
    }
  }

  async function handleRemove() {
    if (!onRemove || removing) return
    setRemoving(true)
    try {
      await onRemove()
    } finally {
      setRemoving(false)
    }
  }

  const isDisabled = disabled || uploading || removing
  const showError = error ?? localError

  return (
    <div className="flex flex-col">
      {label && (
        <label className="text-xs font-medium text-gray-700 mb-1">
          {label}
          {required && <span className="text-rose-500 ml-0.5">*</span>}
        </label>
      )}

      {currentUrl ? (
        // === Preview ===
        <div className={cn(
          'relative bg-white rounded-md ring-1 ring-gray-200 p-3 flex items-center gap-3',
          showError && 'ring-rose-300',
        )}>
          {accept === 'image' ? (
            <img
              src={currentUrl}
              alt={currentName ?? 'preview'}
              className="w-16 h-16 rounded-md object-cover ring-1 ring-gray-100 bg-gray-50"
            />
          ) : (
            <div className="w-16 h-16 rounded-md bg-rose-50 text-rose-600 ring-1 ring-rose-100 flex items-center justify-center shrink-0">
              <FileText size={28} />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <a
              href={currentUrl}
              target="_blank"
              rel="noreferrer"
              className="text-[13px] font-medium text-gray-800 hover:text-brand-600 cursor-pointer break-words block"
            >
              {currentName ?? (accept === 'image' ? 'Visualizar imagem' : 'Abrir PDF')}
            </a>
            <div className="text-[11px] text-gray-500 mt-0.5">
              Clique no nome para abrir em nova aba.
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button
              type="button"
              disabled={isDisabled}
              onClick={() => inputRef.current?.click()}
              className="cursor-pointer text-[11px] font-medium text-gray-600 hover:text-brand-600 px-2 py-1 rounded-md hover:bg-gray-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {uploading ? (
                <span className="inline-flex items-center gap-1"><Loader2 size={11} className="animate-spin" />Enviando</span>
              ) : 'Substituir'}
            </button>
            {onRemove && (
              <button
                type="button"
                disabled={isDisabled}
                onClick={handleRemove}
                aria-label="Remover"
                className="cursor-pointer w-7 h-7 inline-flex items-center justify-center rounded-md text-rose-500 hover:bg-rose-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {removing ? <Loader2 size={13} className="animate-spin" /> : <X size={14} />}
              </button>
            )}
          </div>
        </div>
      ) : (
        // === Drop area ===
        <button
          type="button"
          disabled={isDisabled}
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); if (!isDisabled) setDragging(true) }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault()
            setDragging(false)
            if (isDisabled) return
            const file = e.dataTransfer.files?.[0]
            handleFile(file)
          }}
          className={cn(
            'cursor-pointer w-full rounded-md border-2 border-dashed p-5 flex flex-col items-center justify-center gap-2 text-center transition',
            dragging ? 'border-brand-500 bg-brand-50' : 'border-gray-200 hover:border-gray-300 bg-gray-50/50 hover:bg-gray-50',
            showError && 'border-rose-300 bg-rose-50/40',
            isDisabled && 'opacity-60 cursor-not-allowed',
          )}
        >
          {uploading ? (
            <Loader2 size={22} className="animate-spin text-brand-500" />
          ) : (
            <div className="w-10 h-10 rounded-md bg-white ring-1 ring-gray-200 flex items-center justify-center text-gray-400">
              {accept === 'image' ? <ImageIcon size={18} /> : <Upload size={18} />}
            </div>
          )}
          <div className="text-[13px] font-medium text-gray-700">
            {uploading
              ? 'Enviando arquivo'
              : (<>Clique para selecionar ou <span className="text-brand-600">arraste aqui</span></>)}
          </div>
          <div className="text-[11px] text-gray-500">{acceptLabel}</div>
        </button>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={acceptAttr}
        disabled={isDisabled}
        onChange={(e) => {
          const f = e.target.files?.[0]
          if (f) handleFile(f)
          // Limpa o value para permitir selecionar o mesmo arquivo de novo se necessário.
          e.target.value = ''
        }}
        className="hidden"
      />

      {showError && (
        <div className="text-[11px] text-rose-600 mt-1">{showError}</div>
      )}
      {!showError && hint && (
        <div className="text-[11px] text-gray-500 mt-1">{hint}</div>
      )}
    </div>
  )
}
